import { moveItemInArray } from '@angular/cdk/drag-drop';

/**
 * Applies a drag-and-drop move made in a list that only shows some of the
 * items (e.g. soft-deleted `delFlg = 1` rows are kept in the array for the save
 * payload but not displayed). `previousIndex` / `currentIndex` are positions
 * among the visible items; hidden items keep their relative order after them.
 *
 * Returns a new array, so callers can simply reassign it. Sections derive
 * `sortOrdr` from the array index on save, so the new order is persisted.
 */
export function moveVisibleItem<T>(
  items: T[],
  isVisible: (item: T) => boolean,
  previousIndex: number,
  currentIndex: number,
): T[] {
  const visible = items.filter(isVisible);
  const hidden = items.filter((item) => !isVisible(item));

  if (previousIndex === currentIndex || previousIndex < 0 || previousIndex >= visible.length) {
    return items;
  }

  moveItemInArray(visible, previousIndex, currentIndex);

  return [...visible, ...hidden];
}

/** Sorts loaded rows by their persisted `sortOrdr` (stable; missing values last). */
export function bySortOrder<T extends { sortOrdr?: unknown }>(rows: T[]): T[] {
  const order = (row: T) => {
    const value = Number(row?.sortOrdr);
    return Number.isFinite(value) && value > 0 ? value : Number.MAX_SAFE_INTEGER;
  };

  return [...rows].sort((first, second) => order(first) - order(second));
}
